/**
 * Clasificación de la conexión del encuestador: datos móviles o WiFi.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * POR QUÉ NO SE PREGUNTA AL NAVEGADOR
 * ──────────────────────────────────────────────────────────────────────────
 *
 * La vía evidente sería `navigator.connection.type`, que devolvería
 * directamente 'wifi' o 'cellular'. NO EXISTE: se midió en el navegador y el
 * prototipo de `NetworkInformation` sólo expone
 *
 *   onchange · effectiveType · rtt · downlink · saveData
 *
 * `type` se retiró por ser un vector de huella digital, y en iOS la API
 * completa no está. Lo único que queda es `effectiveType`, que vale '4g',
 * '3g'… pero es una estimación de VELOCIDAD, no de transporte: un WiFi rápido
 * reporta '4g' y uno lento reporta '3g'. Usarlo para decidir si el
 * encuestador está gastando sus datos daría facturación equivocada.
 *
 * Así que la clasificación se hace por el ASN de la dirección desde la que
 * llega la petición, resuelto en el servidor.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * POR QUÉ EL ENCUESTADOR NO PUEDE AJUSTARLA
 * ──────────────────────────────────────────────────────────────────────────
 *
 * Hubo un interruptor manual en su pantalla y se retiró: esta medición
 * alimenta un REEMBOLSO, y quien lo cobra no debe poder modificar la cifra que
 * lo determina. Las correcciones se hacen desde el panel de coordinación.
 *
 * El costo de esa decisión, para que esté a la vista: el ASN no distingue a un
 * encuestador que comparte la conexión de su teléfono a otro aparato, ni un
 * WiFi doméstico que sale por un enlace móvil. Esos casos se clasificarán por
 * la red desde la que navega el dispositivo, no por quién paga los datos. A
 * cambio, nadie puede inflar su propio reembolso.
 */
/**
 * Proveedores con presencia en Baja California.
 *
 * ESTA TABLA ES UN PUNTO DE PARTIDA, NO UNA VERDAD FIJA. Los ASN se
 * reasignan, los operadores se fusionan —Movistar ya migró su tráfico 3G/4G a
 * la red de AT&T— y aparecen proveedores nuevos. Por eso el servidor la lee
 * de la configuración editable (`connectivity.isps`) y esto sólo es el valor
 * con el que se siembra: corregirla no requiere desplegar.
 *
 * Cuando llega un ASN que no está aquí, la conexión se marca `desconocida` y
 * sus bytes NO se suman al contador de datos móviles. Es la decisión
 * conservadora: es preferible subestimar el consumo a cobrarle a la empresa
 * un gasto que quizá no existió, y el ASN desconocido queda listado en el
 * panel para que alguien lo clasifique.
 */
export const BAJA_CALIFORNIA_ISPS = [
    /* ----------------------------- Móviles ------------------------------- */
    {
        asn: 28403,
        name: 'Telcel',
        registeredAs: 'RadioMóvil Dipsa, S.A. de C.V.',
        kind: 'movil',
        notes: 'América Móvil. El operador móvil con mayor cobertura en Tecate.',
    },
    {
        asn: 28469,
        name: 'AT&T México',
        registeredAs: 'AT&T Comunicaciones Digitales, S. de R.L.',
        kind: 'movil',
        notes: 'Absorbió las redes de Iusacell y Nextel. También cursa el tráfico móvil de Movistar.',
    },
    {
        asn: 7438,
        name: 'Movistar',
        registeredAs: 'Pegaso PCS, S.A. de C.V. (Telefónica México)',
        kind: 'movil',
        notes: 'Desde 2019 su tráfico 3G/4G va por la red de AT&T, así que puede aparecer como AS28469.',
    },
    /* ------------------------------- Fijos -------------------------------- */
    {
        asn: 8151,
        name: 'Telmex / Telnor',
        registeredAs: 'Uninet, S.A. de C.V.',
        kind: 'wifi',
        notes: 'TELNOR es la filial de Telmex para Baja California y el noroeste de Sonora, y es el proveedor fijo dominante en Tecate. No se le encontró ASN propio: su tráfico sale por Uninet. Si apareciera uno distinto, se añade aquí.',
    },
    {
        asn: 13999,
        name: 'Megacable',
        registeredAs: 'Mega Cable, S.A. de C.V.',
        kind: 'wifi',
    },
    {
        asn: 28548,
        name: 'izzi',
        registeredAs: 'Cablevisión, S.A. de C.V. (Grupo Televisa)',
        kind: 'wifi',
    },
    {
        asn: 265594,
        name: 'Totalplay',
        registeredAs: 'Televisión Internacional, S.A. de C.V.',
        kind: 'wifi',
    },
];
export const ISP_BY_ASN = Object.fromEntries(BAJA_CALIFORNIA_ISPS.map((i) => [i.asn, i]));
/** Clasifica un ASN contra la tabla vigente. */
export function classifyAsn(asn, table = BAJA_CALIFORNIA_ISPS) {
    if (asn === null || asn === undefined)
        return { kind: 'desconocida', isp: null };
    const isp = table.find((i) => i.asn === asn) ?? null;
    return { kind: isp ? isp.kind : 'desconocida', isp };
}
/** Sólo los datos móviles cuentan: el WiFi no se le cobra a nadie. */
export function billableBytes(samples) {
    return samples.reduce((total, s) => total + (s.connection === 'movil' ? s.bytes : 0), 0);
}
/**
 * Formato legible de un tamaño en bytes.
 *
 * Base 1000 y no 1024: es la que usan los operadores mexicanos al vender y
 * descontar megabytes, y el encuestador va a comparar esta cifra contra la que
 * le muestra su propio teléfono.
 */
export function formatBytes(bytes) {
    if (bytes < 1000)
        return `${bytes} B`;
    const units = ['kB', 'MB', 'GB'];
    let value = bytes / 1000;
    let unit = 0;
    while (value >= 1000 && unit < units.length - 1) {
        value /= 1000;
        unit++;
    }
    return `${value.toFixed(value >= 100 ? 0 : value >= 10 ? 1 : 2)} ${units[unit]}`;
}
//# sourceMappingURL=connectivity.js.map